import { useCallback, useEffect, useState } from "preact/hooks";
import { Layout, Notice, Spinner } from "./components";
import { api } from "./api";
import { useRouter } from "./router";
import type { Route } from "./router";
import type { MintingStatus } from "./types";
import { useWallet } from "./wallet";
import { MintView } from "./views/mint";
import { PortfolioView } from "./views/portfolio";
import { DetailView } from "./views/detail";
import { EditorView } from "./views/editor";
import { PublicSite, type PublicConfig } from "./public-site";

export function App() {
  const { route, navigate } = useRouter();
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [failed, setFailed] = useState(false);
  const refreshConfig = useCallback(async () => {
    setFailed(false);
    try {
      const response = await fetch("/v1/public-config", {signal: AbortSignal.timeout(12000)});
      if (!response.ok) throw new Error("Configuration unavailable");
      const value = await response.json() as PublicConfig;
      if (!Number.isSafeInteger(value.chainId) || value.chainId <= 0) throw new Error("Invalid chain");
      if (value.installURL !== null) {
        const url = new URL(value.installURL);
        if (url.protocol !== "https:" || url.username || url.password || !["apps.apple.com", "testflight.apple.com"].includes(url.hostname)) throw new Error("Invalid Apple install URL");
      }
      setConfig(value);
    } catch { setConfig(null); setFailed(true); }
  }, []);
  useEffect(() => { void refreshConfig(); }, [refreshConfig]);
  const ownerRoute = ["portfolio", "detail", "editor"].includes(route.name) || (route.name === "mint" && config?.chainId === 31337);
  if (!ownerRoute) return <PublicSite route={route} config={config} configFailed={failed} retryConfig={() => void refreshConfig()} />;
  return <OwnerApp route={route} navigate={navigate} config={config} />;
}

/** No wallet provider is queried or subscribed on ordinary public pages. */
function OwnerApp(props: { route: Route; navigate(path: string): void; config: PublicConfig | null }) {
  const wallet = useWallet();
  const [status, setStatus] = useState<MintingStatus | null>(null);
  const [error, setError] = useState(false);
  const refreshStatus = useCallback(async () => {
    try { setStatus(await api.mintingStatus()); setError(false); }
    catch { setError(true); }
  }, []);
  useEffect(() => { void refreshStatus(); }, [refreshStatus]);
  const {route, navigate} = props;
  let content;
  if (route.name === "mint") content = <MintView status={status} wallet={wallet} navigate={navigate} refreshStatus={refreshStatus} />;
  else if (route.name === "portfolio") content = <PortfolioView wallet={wallet} navigate={navigate} />;
  else if (route.name === "detail" && route.qyoo !== undefined) content = <DetailView qyoo={route.qyoo} wallet={wallet} navigate={navigate} />;
  else if (route.name === "editor" && route.qyoo !== undefined) content = <EditorView qyoo={route.qyoo} wallet={wallet} navigate={navigate} />;
  return <Layout production={!props.config || props.config.chainId === 1} developmentMint={props.config?.chainId === 31337} wallet={wallet} network={status?.chain.network ?? null} navigate={navigate}>
    {status === null && !error && <Spinner label="Reading network configuration…" />}
    {error && <div class="global-notice"><Notice kind="error">The owner service could not be reached. <button onClick={() => void refreshStatus()}>Retry</button></Notice></div>}
    {content}
  </Layout>;
}
